import { Reveal } from '../components/Reveal';

const DOCS = {
  privacy: {
    title: 'Privacy Policy',
    intro: 'This Privacy Policy explains how Settalkar ("we", "our") collects, uses, and protects your personal information when you use settalkar.com or contact us on WhatsApp at +91 8383854950.',
    sections: [
      {
        h: 'Information We Collect',
        p: [
          'When you submit a challan check or callback request we collect: your vehicle registration number, your state or city, your mobile number (WhatsApp), and your name where provided.',
          'If you share challan photos, court notices, or documents on WhatsApp, we retain copies solely to assess and process your case.',
        ],
      },
      {
        h: 'How We Use Your Information',
        p: [
          'To assess your pending challans and prepare a legal settlement quote.',
          'To represent your case before Virtual Courts, Lok Adalats, and traffic authorities.',
          'To contact you on WhatsApp or by phone regarding your request. We never send unrelated marketing without consent.',
        ],
      },
      {
        h: 'Sharing of Information',
        p: [
          'Your details are shared only with the advocates and legal staff handling your case, and with the concerned court or government portal as required by the statutory process.',
          'We do not sell, rent, or trade your personal information to any third party.',
        ],
      },
      {
        h: 'Data Security & Retention',
        p: [
          'We apply reasonable technical and organizational safeguards to protect your data. Case records are retained only as long as needed to complete your matter and to satisfy legal record-keeping obligations.',
          'You may request deletion of your personal data by writing to support@settalkar.com, subject to statutory retention requirements.',
        ],
      },
      {
        h: 'Contact',
        p: ['For any privacy question or request, write to support@settalkar.com or WhatsApp +91 8383854950.'],
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    intro: 'These Terms govern your use of settalkar.com and the legal facilitation services provided by Settalkar. By submitting a request, you agree to these Terms.',
    sections: [
      {
        h: 'Nature of Service',
        p: [
          'Settalkar is a private legal technology and consultancy platform. We facilitate lawful traffic challan settlement, dispute, and disposal through Virtual Courts, Lok Adalats, and statutory compounding processes, using licensed advocates.',
          'Settalkar is not a government body and does not guarantee any specific reduction, waiver, or timeline. Outcomes depend on the concerned court, forum, and facts of each case.',
        ],
      },
      {
        h: 'Your Obligations',
        p: [
          'You confirm that the information and documents you provide are true, accurate, and relate to you or a vehicle you are authorized to act for.',
          'You authorize Settalkar and its empanelled advocates to file, appear, communicate, and settle on your behalf for the specific matters you engage us for.',
        ],
      },
      {
        h: 'Fees & Payment',
        p: [
          'Professional fees are communicated upfront as a flat, transparent amount before any work begins. There are no hidden charges.',
          'Court compounding amounts, statutory fines, and government fees (where applicable) are separate from professional fees and are always disclosed before payment.',
        ],
      },
      {
        h: 'Limitation of Liability',
        p: [
          'To the maximum extent permitted by law, Settalkar is liable only for the professional fee paid for the specific matter, and only for direct losses caused by proven negligence on our part.',
        ],
      },
      {
        h: 'Contact',
        p: ['Questions about these Terms: support@settalkar.com or WhatsApp +91 8383854950.'],
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    intro: 'Settalkar operates on a transparent flat-fee model. This policy explains when a refund applies.',
    sections: [
      {
        h: '100% Refund — Matter Not Settled',
        p: [
          'If we are unable to legally settle or dispose of the specific challan you engaged us for (and it remains outstanding through no default of yours), you receive a 100% refund of the professional fee for that matter.',
        ],
      },
      {
        h: 'When Refunds Do Not Apply',
        p: [
          'After a matter has been successfully settled or disposed of, and the official court receipt has been issued.',
          'Where you provided incorrect vehicle, challan, or contact information and the matter could not be processed after reasonable follow-up.',
          'Government fines, statutory compounding amounts, and court-ordered payments already remitted to authorities.',
        ],
      },
      {
        h: 'Refund Timeline',
        p: [
          'Approved refunds are processed to the original payment method within 7–10 business days.',
          'To request a refund, write to support@settalkar.com with your registered mobile number and vehicle number.',
        ],
      },
    ],
  },
  disclaimer: {
    title: 'Legal Disclaimer',
    intro: 'Please read this disclaimer carefully before using settalkar.com.',
    sections: [
      {
        h: 'Not a Government Website',
        p: [
          'Settalkar is a private legal technology and consultancy service. We are not affiliated with, endorsed by, or acting on behalf of any government body, RTO, traffic police, court, or Parivahan.',
          'All challan settlements are carried out strictly through statutory legal processes — Virtual Court, Lok Adalat, and official compounding — before the appropriate forums.',
        ],
      },
      {
        h: 'No Guarantee of Outcome',
        p: [
          'Reductions, waivers, and disposal outcomes are decided solely by the concerned court, Lok Adalat bench, or authority. Past results, including the number of challans disposed, do not guarantee any particular outcome in your case.',
        ],
      },
      {
        h: 'Informational Content',
        p: [
          'Content on this website is for general information and does not constitute legal advice for your specific situation. Share your challan details for an assessment before acting.',
          'This site may contain links to external government portals (such as Parivahan) for your convenience; we are not responsible for their content or availability.',
        ],
      },
      {
        h: 'Contact',
        p: ['For clarifications: support@settalkar.com or WhatsApp +91 8383854950.'],
      },
    ],
  },
};

export default function LegalPage({ docKey }) {
  const doc = DOCS[docKey];
  if (!doc) return null;

  return (
    <main className="bg-[#F8FAFC] pt-14 pb-24" data-testid={`legal-page-${docKey}`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-orange">Settalkar — settalkar.com</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-brand-ink">{doc.title}</h1>
          <p className="mt-5 text-base leading-relaxed text-slate-500">{doc.intro}</p>
        </Reveal>

        <div className="mt-12 flex flex-col gap-8">
          {doc.sections.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.05}>
              <section className="rounded-2xl bg-white border border-slate-200 p-7 sm:p-8">
                <h2 className="font-display text-2xl font-bold text-brand-blue flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand-blue/10 text-brand-blue text-sm font-bold">
                    {i + 1}
                  </span>
                  {s.h}
                </h2>
                <div className="mt-4 flex flex-col gap-3">
                  {s.p.map((para, j) => (
                    <p key={j} className="text-sm sm:text-[15px] leading-relaxed text-slate-600">
                      {para}
                    </p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}
